'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * TableOfContents — sticky left rail (200px wide, top: 80px).
 *
 * Spec: design-references/article.html `.a-toc` + progress bar.
 *   - "In this review" eyebrow above items
 *   - IntersectionObserver on `[data-article-section]` elements
 *     (root margin `-20% 0px -70% 0px` keeps ~20% upper band as the
 *     "active zone" — gives a natural feel when a section is mostly
 *     in view but before it fully hits the top).
 *   - Numbered items with 2-digit pad
 *   - Active item: teal left border + light teal bg
 *   - Bottom: "Progress N%" driven by scroll pct of document
 */

export interface TocItem {
  id: string;
  title: string;
}

export interface TableOfContentsProps {
  items: TocItem[];
  className?: string;
  /** Optional CTA rendered below the progress bar in the sticky rail. */
  cta?: React.ReactNode;
}

export function TableOfContents({ items, className, cta }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const [progressPct, setProgressPct] = useState(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Scroll-spy via IntersectionObserver.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sections = items
      .map((it) => document.getElementById(it.id))
      .filter((el): el is HTMLElement => !!el);
    if (sections.length === 0) return;

    const visibleIds = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visibleIds.add(e.target.id);
          else visibleIds.delete(e.target.id);
        }
        // Pick the first (top-most) visible section as active.
        for (const it of itemsRef.current) {
          if (visibleIds.has(it.id)) {
            setActiveId(it.id);
            return;
          }
        }
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [items]);

  // Reading-progress bar in the TOC footer — same pct as the top bar.
  useEffect(() => {
    let raf = 0;
    const compute = () => {
      const h = document.documentElement;
      const scrollable = h.scrollHeight - window.innerHeight;
      const scrolled = scrollable > 0 ? window.scrollY / scrollable : 0;
      setProgressPct(Math.max(0, Math.min(1, scrolled)));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const renderedItems = useMemo(() => items.map((it, i) => ({ ...it, n: i + 1 })), [items]);

  return (
    <aside
      className={cn('self-start sticky top-20 font-mono text-[11px]', className)}
      aria-label="Table of contents"
    >
      {cta && <div className="mb-6">{cta}</div>}
      <div className="text-[10px] tracking-[0.14em] uppercase text-slate pb-3 mb-3.5 border-b border-border">
        In this review
      </div>
      <nav className="flex flex-col" aria-label="Article sections">
        {renderedItems.map((it) => (
          <a
            key={it.id}
            href={`#${it.id}`}
            aria-current={it.id === activeId ? 'true' : undefined}
            className={cn(
              'flex justify-between gap-2 no-underline py-2 pl-3',
              'border-l-2 transition-all duration-fast leading-[1.4] tracking-[0.02em]',
              it.id === activeId
                ? 'text-warm-navy font-medium border-aviado-teal bg-aviado-teal/[0.04]'
                : 'text-slate border-transparent hover:text-ink'
            )}
          >
            <span>{it.title}</span>
            <span className="tabular-nums opacity-60 shrink-0">
              {String(it.n).padStart(2, '0')}
            </span>
          </a>
        ))}
      </nav>

      <div
        className="mt-5 pt-3.5 border-t border-border font-mono text-[10.5px] tracking-[0.04em] text-slate"
        aria-hidden="true"
      >
        <div className="flex justify-between">
          <span>Progress</span>
          <span className="tabular-nums">{Math.round(progressPct * 100)}%</span>
        </div>
        <div className="mt-1.5 h-[3px] rounded-sm bg-border overflow-hidden">
          <div
            className="h-full bg-aviado-teal transition-[width] duration-[120ms] ease-linear"
            style={{ width: `${progressPct * 100}%` }}
          />
        </div>
      </div>
    </aside>
  );
}

export default TableOfContents;
